/**
 * UploadZone - Shared upload zone UI component
 * 
 * Provides consistent upload zone behavior across all tools:
 * - Drag and drop handling
 * - File input handling
 * - Preview display
 * - Clear functionality
 * 
 * Used by both ImageConverterTool and QuickConverterTool to ensure UI consistency
 */

export interface UploadZoneConfig {
  containerId: string;
  fileInputId: string;
  acceptTypes: string;
  onFileSelected: (file: File) => void;
  maxFileSize?: number; // in bytes, defaults to 50MB
  formatLabel?: string; // e.g., "PNG", "JPG" for user messages
}

export class UploadZone {
  private config: UploadZoneConfig;
  private uploadZone: HTMLElement | null = null;
  private fileInput: HTMLInputElement | null = null;
  private uploadPlaceholder: HTMLElement | null = null;
  private uploadedPreview: HTMLElement | null = null;
  private previewImage: HTMLImageElement | null = null;
  private clearButton: HTMLButtonElement | null = null;
  
  constructor(config: UploadZoneConfig) {
    this.config = {
      ...config,
      maxFileSize: config.maxFileSize || 50 * 1024 * 1024, // 50MB default
    };
  }

  /**
   * Initialize the upload zone with event listeners
   */
  init(): void {
    this.initializeElements();
    this.setupEventListeners();
  }

  /**
   * Initialize DOM element references
   */
  private initializeElements(): void {
    this.uploadZone = document.getElementById(this.config.containerId);
    this.fileInput = document.getElementById(this.config.fileInputId) as HTMLInputElement;
    this.uploadPlaceholder = document.getElementById('uploadPlaceholder');
    this.uploadedPreview = document.getElementById('uploadedImagePreview');
    this.previewImage = document.getElementById('previewImage') as HTMLImageElement;
    this.clearButton = document.getElementById('clearImageBtn') as HTMLButtonElement;

    if (!this.uploadZone || !this.fileInput) {
      console.error('UploadZone: Required elements not found');
      return;
    }

    // Set accept attribute
    this.fileInput.accept = this.config.acceptTypes;
  }

  /**
   * Setup all event listeners for upload zone interactions
   */
  private setupEventListeners(): void {
    if (!this.uploadZone || !this.fileInput) {
      console.error('UploadZone: Cannot setup listeners, elements not initialized');
      return;
    }

    // Click to upload (on upload zone)
    this.uploadZone.addEventListener('click', (e) => {
      // Only trigger if clicking the zone itself or placeholder, not the preview image
      const target = e.target as HTMLElement;
      if (this.uploadedPreview?.style.display === 'block' && 
          this.uploadedPreview.contains(target)) {
        // Don't open file dialog when clicking on uploaded preview
        return;
      }
      this.fileInput?.click();
    });

    // Browse link click (if exists)
    const browseLink = this.uploadZone.querySelector('.browse-link');
    if (browseLink) {
      browseLink.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.fileInput?.click();
      });
    }

    // File input change
    this.fileInput.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        this.handleFileSelection(file);
      }
    });

    // Drag and drop
    this.uploadZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.uploadZone?.classList.add('drag-over');
    });

    this.uploadZone.addEventListener('dragleave', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.uploadZone?.classList.remove('drag-over');
    });

    this.uploadZone.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.uploadZone?.classList.remove('drag-over');

      const file = e.dataTransfer?.files[0];
      if (file) {
        this.handleFileSelection(file);
      }
    });

    // Prevent default drag behavior on document
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      document.addEventListener(eventName, (e) => {
        e.preventDefault();
        e.stopPropagation();
      }, false);
    });

    // Clear button
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => {
        this.clear();
      });
    }
  }

  /**
   * Handle file selection with validation
   */
  private handleFileSelection(file: File): void {
    // Validate file size
    if (file.size > this.config.maxFileSize!) {
      const sizeMB = Math.round(this.config.maxFileSize! / (1024 * 1024));
      alert(`File is too large. Maximum size is ${sizeMB}MB.`);
      return;
    }

    // Call the callback with the selected file
    this.config.onFileSelected(file);
  }

  /**
   * Show preview of uploaded image
   * Consistent styling across all tools
   */
  showPreview(imageSrc: string): void {
    if (!this.previewImage) return;

    this.previewImage.src = imageSrc;
    
    // Consistent styling - matches Image Converter
    this.previewImage.style.maxWidth = '100%';
    this.previewImage.style.height = 'auto';
    this.previewImage.style.borderRadius = '8px';
    this.previewImage.style.display = 'block';

    // Show preview, hide placeholder
    if (this.uploadPlaceholder) {
      this.uploadPlaceholder.style.display = 'none';
    }
    if (this.uploadedPreview) {
      this.uploadedPreview.style.display = 'block';
    }

    // Show clear button
    if (this.clearButton) {
      this.clearButton.style.display = 'flex';
    }
  }

  /**
   * Clear the upload zone and reset to initial state
   * Consistent behavior across all tools
   */
  clear(): void {
    // Reset preview image
    if (this.previewImage) {
      this.previewImage.src = '';
    }

    // Show placeholder, hide preview
    if (this.uploadPlaceholder) {
      this.uploadPlaceholder.style.display = 'flex';
    }
    if (this.uploadedPreview) {
      this.uploadedPreview.style.display = 'none';
    }

    // Hide clear button
    if (this.clearButton) {
      this.clearButton.style.display = 'none';
    }

    // Reset file input
    if (this.fileInput) {
      this.fileInput.value = '';
    }
  }

  /**
   * Check if a file is currently uploaded
   */
  hasFile(): boolean {
    return this.fileInput?.value !== '';
  }

  /**
   * Get the file input element
   */
  getFileInput(): HTMLInputElement | null {
    return this.fileInput;
  }

  /**
   * Cleanup and remove event listeners
   */
  destroy(): void {
    // Event listeners will be automatically removed when elements are removed from DOM
    // For explicit cleanup, we could store listener references and remove them here
    this.clear();
  }
}
